import * as React from 'react';
import { observer } from 'mobx-react';
import autobind from "autobind-decorator";

import { DropdownView } from 'src/core/ui/dropdown.view';
import { resolve } from 'inversify-react';
import { RulesReflectionService } from 'src/app/rules/rules/rules.reflection.service';
import { Tabs, Radio, Row, Col } from 'antd';

const TabPane = Tabs.TabPane;
const RadioGroup = Radio.Group;

class Token {

	public value: any;

	str() {
		return '*';
	}


	type(): 'literal' | 'range' | 'increment' | 'list' | 'none' | 'any' {
		return 'none';
	}
}

class Any extends Token {

	str() {
		return "*";
	}

	type(): 'literal' | 'range' | 'increment' | 'list' | 'none' | 'any' {
		return 'any';
	}
}

class Literal extends Token{
	
	constructor(val: string) {
		super();
		this.value = val;
	}

	str() {

		return this.value;
	}

	type(): 'literal' | 'range' | 'increment' | 'list' | 'none' | 'any'  {
		return 'literal';
	}
}

class Range extends Token {
	

	constructor(start: string, end: string) {
		super();
		this.value = [start, end];
	}

	str() {
		return this.value[0] + '-' + this.value[1];
	}

	type(): 'literal' | 'range' | 'increment' | 'list' | 'none' | 'any'  {
		return 'range';
	}
}

class Increment extends Token {
	

	constructor(value: string) {
		super();
		this.value = value;

	}

	str() {
		return '0/' + this.value;
	}

	type(): 'literal' | 'range' | 'increment' | 'list' | 'none' | 'any'  {
		return 'increment';
	}
}

class List extends Token {
	
	constructor(value: string[]) {
		super();
		this.value = value;
	}

	str() {
		return this.value.join(",");
	}

	type(): 'literal' | 'range' | 'increment' | 'list' | 'none' | 'any' {
		return 'list';
	}
}

class Parser {

	public parse(chain: string): Token[] {

		if (!chain)
			return [];

		var chunks = chain.split(' ');
		var result = [];

		for (var c of chunks) {

			if ('*' === c) {
				result.push(new Any());
				continue;
			}
			if (/^\d+$/.test(c)) {
				result.push(new Literal(c));
				continue;
			}
			if (/^0\/(\d+)$/.test(c)) {

				const reg = /^0\/(\d+)$/;
				const value = reg.exec(c);

				result.push(new Increment(value[1]));
				continue;
			}
			if (/^(.*)-(.*)$/.test(c)) {

				const reg = /^(.*)-(.*)$/;
				const value = reg.exec(c);

				result.push(new Range(value[1], value[2]));
				continue;
			}

			const lResult = this.list(c);
			if (lResult.length > 0) {
				result.push(new List(lResult));

				continue;
			}
			
		}

		return result;
	}

	list(chunk: string): string[] {
		
		chunk = chunk.trim();

		if (chunk.length == 0)
			return [];

		var cumul = '';
		var result = [];

		for (var i = 0; i < chunk.length; i++) {
			if (chunk[i] !== ',')
				cumul += chunk[i];
			else {
				i += 1;
				break;
			}
		}

		result.push(cumul);

		result = result.concat(this.list(chunk.substr(i)));

		return result;
	}

}

interface CronBuilderProps {
	mode: ControlMode;
	cronScript: string;
	onChanged: (script: string) => void;
}

interface CronBuilderState {
	cron: string;
	seconds: Token;
	minutes: Token;
	hours: Token;
	monthDay: Token;
	month: Token;
	weekDay: Token;

	periodType: 'minutes' | 'hours';
	onType: 'weekly' | 'daymonth' | 'monthly';
}

@observer
export class CronBuilder extends React.Component<CronBuilderProps, CronBuilderState> {
	constructor(props) {
		super(props);

		this.state = {
			cron: this.props.cronScript,
			seconds: new Literal("0"),
			minutes: new Any(),
			hours: new Any(),
			monthDay: new Any(),
			month: new Any(),
			weekDay: new Literal("?"),

			periodType: 'minutes',
			onType: 'weekly'
		}
	}

	

	weekDays = [
		{ value: 'MON', label: 'Mondays', order: 0 },
		{ value: 'TUE', label: 'Tuesday', order: 1 },
		{ value: 'WED', label: 'Wednesdays', order: 2 },
		{ value: 'THU', label: 'Thursday', order: 3 },
		{ value: 'FRI', label: 'Friday', order: 4 },
		{ value: 'SAT', label: 'Saturday', order: 5 },
		{ value: 'SUN', label: 'Sunday', order: 6 },
	];


	monthDays = [
		{ value: '1', label: '1st', order: 0 },
		{ value: '2', label: '2nd', order: 1 },
		{ value: '3', label: '3rd', order: 2 },
		{ value: '4', label: '4th', order: 3 },
		{ value: '5', label: '5th', order: 4 },
		{ value: '6', label: '6th', order: 5 },
		{ value: '7', label: '7th', order: 6 },
		{ value: '8', label: '8th', order: 7 },
		{ value: '9', label: '9th', order: 8 },
		{ value: '10', label: '10th', order: 9},
		{ value: '11', label: '11st', order: 10 },
		{ value: '12', label: '12nd', order: 11 },
		{ value: '13', label: '13rd', order: 12 },
		{ value: '14', label: '14th', order: 13 },
		{ value: '15', label: '15th', order: 14 },
		{ value: '16', label: '16th', order: 15 },
		{ value: '17', label: '17th', order: 16 },
		{ value: '18', label: '18th', order: 17 },
		{ value: '19', label: '19th', order: 18 },
		{ value: '20', label: '20th', order: 19 },
		{ value: '21', label: '21st', order: 20 },
		{ value: '22', label: '22nd', order: 21 },
		{ value: '23', label: '23rd', order: 22 },
		{ value: '24', label: '24th', order: 23 },
		{ value: '25', label: '25th', order: 24 },
		{ value: '26', label: '26th', order: 25 },
		{ value: '27', label: '27th', order: 26 },
		{ value: '28', label: '28th', order: 27 },
		{ value: '29', label: '29th', order: 28 },
		{ value: '30', label: '30th', order: 29 },
		{ value: '31', label: '31st', order: 30 },
	];


	months = [
		{ value: 'JAN', label: 'Jannuary', order: 0 },
		{ value: 'FEB', label: 'February', order: 1 },
		{ value: 'MAR', label: 'March', order: 2 },
		{ value: 'APR', label: 'April', order: 3 },
		{ value: 'MAY', label: 'May', order: 4 },
		{ value: 'JUN', label: 'June', order: 5 },
		{ value: 'JUL', label: 'July', order: 6 },
		{ value: 'AUG', label: 'August', order: 7 },
		{ value: 'SEP', label: 'September', order: 8 },
		{ value: 'OCT', label: 'October', order: 9 },
		{ value: 'NOV', label: 'November', order: 10 },
		{ value: 'DEC', label: 'December', order: 11 },
	];

	parse(script: string) {
		var p = new Parser();

		var tokens = p.parse(script);

		if (tokens.length < 6)
			tokens = [new Literal("0"), new Any(), new Any(), new Any(), new Any(), new Literal("?")];

		this.setState({
			seconds: tokens[0],
			minutes: tokens[1],
			hours: tokens[2],
			monthDay: tokens[3],
			month: tokens[4],
			weekDay: tokens[5]
		})
	}

	public componentDidMount() {

		this.parse(this.state.cron);
	}

	

	getNumberOptions(fr: number, end: number, filter: (i: number) => boolean): {value: number, label: string}[] {

		var options = [];

		for (var i = fr; i <= end; i++)
			if(filter(i))
				options.push({value: i, label: i.toString()})

		return options;

	}

	@autobind
	codeCron() {

		return this.state.seconds.str() + " "
			+ this.state.minutes.str() + ' '
			+ this.state.hours.str() + ' '
			+ this.state.monthDay.str() + ' '
			+ this.state.month.str() + ' '
			+ this.state.weekDay.str();

	}

	@autobind
	setWeekDays(e: any[]) {

		e = e.sort((a, b) => a.order - b.order);

		if (e.length == 0)
			this.setState({ weekDay: new Literal("?") }, this.notifyChange)
		else  {
			
			const l = new List(e.map((item, index) => item.value));

			this.setState({ weekDay: l }, this.notifyChange);
		}
		
		
	}


	@autobind
	setMonth(e: any[]) {

		e = e.sort((a, b) => a.order - b.order);

		if (e.length == 0)
			this.setState({ month: new Any() }, this.notifyChange)
		else {

			const l = new List(e.map((item, index) => item.value));

			this.setState({ month: l }, this.notifyChange);
		}


	}

	@autobind
	setDays(e: any[]) {

		e = e.sort((a, b) => a.order - b.order);

		if (e.length == 0)
			this.setState({ monthDay: new Any() }, this.notifyChange)
		else {

			const l = new List(e.map((item, index) => item.value));

			this.setState({ monthDay: l }, this.notifyChange);
		}


	}

	formatCron(e: string) {
		if (e == 'p')
			this.setState({ hours: new Any(), minutes: new Increment("1"), monthDay: new Any(), month: new Any() });
		if (e == 'fr')
			this.setState({ hours: new Range("1", "2"), minutes: new Increment("1"), monthDay: new Any(), month: new Any() });
		if (e == 'fix')
			this.setState({hours: new Literal("1"), minutes: new Literal("00")})
	}

	@autobind
	notifyChange() {

		this.props.onChanged(this.codeCron());

	}

	render() {

		const onComponent = (<React.Fragment>
			<RadioGroup
				disabled={this.props.mode == 'display'} value={this.state.onType} onChange={e => this.setState({ onType: e.target.value as 'monthly' | 'weekly' | 'daymonth' })}>
				<Radio value='weekly'>On week days</Radio>
				<Radio value='monthly'>On month</Radio>
				<Radio value='daymonth'>On month days</Radio>
			</RadioGroup>

			{this.state.onType == 'weekly' && < DropdownView
				label=""
				multi
				disabled={this.props.mode == 'display'}
				model={(this.state.weekDay as any).value}
				options={this.weekDays}
				onChanged={(e) => {
					this.setWeekDays(e);
				}}
			/>}
			{this.state.onType == 'monthly' && < DropdownView
				label=""
				multi
				disabled={this.props.mode == 'display'}
				model={(this.state.month as any).value}
				options={this.months}
				onChanged={(e) => {
					this.setMonth(e);
				}}
			/>}
			{this.state.onType == 'daymonth' && < DropdownView
				label=""
				multi
				disabled={this.props.mode == 'display'}
				model={(this.state.monthDay as any).value}
				options={this.monthDays}
				onChanged={(e) => {
					this.setDays(e);
				}}
			/>}

		</React.Fragment>);

		return <div>
			<Tabs onChange={e => this.formatCron(e)}>
				<TabPane key="p" tab='Periodically'>
					<Row style={{ display: 'flex', alignItems: 'center', flexDirection: 'row', flexWrap: 'wrap' }}>
						<Col xs={3}>
							{this.state.periodType == 'minutes' && < DropdownView
								label="Every (min)"
								disabled={this.props.mode == 'display'}
								model={(this.state.minutes as any).value}
								options={this.getNumberOptions(1, 60, i => 60 % i == 0)}
	
								onChanged={(e) => e && this.setState({ minutes: new Increment(e.label), hours: new Any() }, this.notifyChange)}

							/>}

							{this.state.periodType == 'hours' && this.state.hours.type() !== 'range' && < DropdownView
								label="Every (hour)"
								disabled={this.props.mode == 'display'}
								model={(this.state.hours as any).value}
								options={this.getNumberOptions(1, 24, i => 24 % i == 0)}
								onChanged={(e) => e && this.setState({ hours: new Increment(e.label), minutes: new Literal("0") }, this.notifyChange)}

							/>}
						</Col>
						<Col xs={8} style={{margin: '5px'}}>
							<RadioGroup
								disabled={this.props.mode == 'display'} value={this.state.periodType} onChange={e => this.setState({ periodType: e.target.value as 'minutes' | 'hours' })}>
								<Radio value='minutes'>Minutes</Radio>
								<Radio value='hours'>Hours</Radio>
							</RadioGroup>
						</Col>
						<Col xs={12}>
							{onComponent}
						</Col>
					</Row>
				</TabPane>

				<TabPane key='fr' tab='Periodically with a time frame'>
					<Row style={{ display: 'flex', alignItems: 'center', flexDirection: 'row', flexWrap: 'wrap' }}>
						<Col xs={3}>
							< DropdownView
								disabled={this.props.mode == 'display'}
								label="Every (min)"
								model={(this.state.minutes as any).value}
								options={this.getNumberOptions(1, 60, i => 60 % i == 0)}
								onChanged={(e) => e && this.setState({ minutes: new Increment(e.label) }, this.notifyChange)}

							/>
							
						</Col>
						{this.state.hours.type() == 'range' && < Col xs={5} style={{ margin: '5px' }}>
							< DropdownView
								disabled={this.props.mode == 'display'}
								label="Starting at (hour)"
								model={(this.state.hours as any).value[0]}
								options={this.getNumberOptions(1, 24, i => true)}
								onChanged={(e) => e && this.setState({ hours: new Range(e.label, (this.state.hours as any).value[1]) }, this.notifyChange)}

							/>
						</Col>}
						{this.state.hours.type() == 'range' && 
							<Col xs={5} style={{ margin: '5px' }}>
							< DropdownView
								disabled={this.props.mode == 'display'}
									label="Ending at (hour)"
									model={(this.state.hours as any).value[1]}
									options={this.getNumberOptions(1, 24, i => true)}
									onChanged={(e) => e && this.setState({ hours: new Range((this.state.hours as any).value[0], e.label) }, this.notifyChange)}

								/>
							</Col>}
						<Col xs={11}>
							{onComponent}
						</Col>
					</Row>
				</TabPane>

				<TabPane key='fix' tab='At a recurring fixed time'>
					<Row style={{ display: 'flex', alignItems: 'center', flexDirection: 'row', flexWrap: 'wrap' }}>
						<Col xs={5} style={{ margin: '6px' }}>
							{this.state.hours.type() == 'literal' && < DropdownView
								label="At (hour)"
								disabled={this.props.mode == 'display'}
								model={(this.state.hours as any).value}
								options={this.getNumberOptions(1, 24, i => true)}
								onChanged={(e) => e && this.setState({ hours: new Literal(e.label) }, this.notifyChange)}

							/>}

						</Col>

						<Col xs={5} style={{ margin: '6px' }}>
							< DropdownView
								label="Month day"
								multi
								disabled={this.props.mode == 'display'}
								model={(this.state.monthDay as any).value}
								options={this.monthDays}
								onChanged={(e) => {
									this.setDays(e);
								}}
							/>
						</Col>

						<Col xs={8} style={{ margin: '6px' }}>
							< DropdownView
								label="Month"
								multi
								disabled={this.props.mode == 'display'}
								model={(this.state.month as any).value}
								options={this.months}
								onChanged={(e) => {
									this.setMonth(e);
								}}
							/>
						</Col>
					</Row>
				</TabPane>
			</Tabs>

			<p><strong>Cron script: </strong> {this.codeCron()}</p>
			<p>Using <a href='https://www.quartz-scheduler.net/documentation/quartz-2.x/tutorial/crontriggers.html'>Quartz.Net</a> specification </p>
		</div>
	}
}



// WEBPACK FOOTER //
// ./src/app/rules/rules/form/parts/cron-builder.tsx